// Small formatting helpers shared across the invoice UI.

export function money(amount, currency = 'EUR') {
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat('nl-BE', {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${n.toFixed(0)} ${currency}`;
  }
}

export function moneyExact(amount, currency = 'EUR') {
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat('nl-BE', { style: 'currency', currency }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

export function formatNumber(value) {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat('nl-BE').format(n);
  } catch {
    return String(n);
  }
}

export function fmtDate(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('nl-BE', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return d;
  }
}

// Relative "due in / overdue by" phrasing from the server-computed days_overdue
// (positive = overdue, negative = due in future).
export function duePhrase(daysOverdue) {
  if (daysOverdue === null || daysOverdue === undefined) return 'no due date';
  if (daysOverdue > 0) return `${daysOverdue}d overdue`;
  if (daysOverdue === 0) return 'due today';
  return `due in ${Math.abs(daysOverdue)}d`;
}

export function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export const BLOCKED = {
  1: { label: 'Normal', variant: 'default' },
  2: { label: 'At risk', variant: 'warning' },
  3: { label: 'Blocked', variant: 'destructive' },
  4: { label: 'Proforma', variant: 'info' },
};

export const PRIORITY = {
  critical: { label: 'Critical', variant: 'destructive' },
  high: { label: 'High', variant: 'warning' },
  medium: { label: 'Medium', variant: 'accent' },
  low: { label: 'Low', variant: 'default' },
};

export const PAYMENT = {
  not_paid: { label: 'Not paid', variant: 'outline' },
  draft: { label: 'Draft', variant: 'warning' },      // expense note not yet posted in Odoo
  in_payment: { label: 'In payment', variant: 'accent' },
  partial: { label: 'Partial', variant: 'accent' },
  paid: { label: 'Paid', variant: 'success' },
  reversed: { label: 'Reversed', variant: 'default' },
};

// Currencies actually used on vendor documents / credit notes in Odoo (since
// 2024), ordered by how often they occur. EUR is the default. Add a code here if
// a new one ever comes up - the amount formatters handle any ISO code.
export const CURRENCIES = [
  ['EUR', 'Euro'], ['GBP', 'Pound sterling'], ['USD', 'US dollar'], ['CHF', 'Swiss franc'],
  ['AUD', 'Australian dollar'], ['CAD', 'Canadian dollar'], ['HKD', 'Hong Kong dollar'],
  ['AED', 'UAE dirham'], ['SGD', 'Singapore dollar'], ['SEK', 'Swedish krona'],
  ['DKK', 'Danish krone'], ['PLN', 'Polish złoty'], ['TWD', 'New Taiwan dollar'],
  ['NOK', 'Norwegian krone'], ['CZK', 'Czech koruna'], ['ZAR', 'South African rand'],
  ['JPY', 'Japanese yen'], ['NZD', 'New Zealand dollar'], ['BRL', 'Brazilian real'],
  ['SAR', 'Saudi riyal'], ['HUF', 'Hungarian forint'], ['KRW', 'South Korean won'],
  ['MXN', 'Mexican peso'], ['ILS', 'Israeli new shekel'], ['INR', 'Indian rupee'],
  ['CNY', 'Chinese yuan'], ['RON', 'Romanian leu'], ['QAR', 'Qatari riyal'],
];

export const PROFORMA_STATUS = {
  pending: { label: 'To pay', variant: 'warning' },
  paid: { label: 'Paid · to finalize', variant: 'info' },
  finalized: { label: 'Finalized', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'default' },
};
